import { useState } from "react";
import { Dialog, DialogFooter, FailedCallout, ReasonField, useReason, useToast } from "@amber/ui";
import { structureResponseSchema, type CarriedSummary, type EvidenceRecord } from "../../contracts/index";
import { OpenReports, type Editing } from "../editing/Editing";
import { EVIDENCE_ERROR_FALLBACK, REQUIRED_REASON } from "../format";
import { useEvidenceCommand } from "../queries";
import { describe, secs, type Draft } from "./timingDraft";

const CARRIED_STATE: Record<CarriedSummary["state"], string> = {
  applied: "carried over",
  pending: "note left",
  deferred: "waiting for its editor",
};

export function TimingReview({
  record,
  communicationId,
  communicationOrdinal,
  baseRevision,
  base,
  drafts,
  editing,
  onClose,
  onSaved,
}: {
  record: EvidenceRecord;
  communicationId: string;
  communicationOrdinal: number;
  baseRevision: string;
  base: readonly Draft[];
  drafts: readonly Draft[];
  editing: Editing;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const reason = useReason(null, REQUIRED_REASON);
  const [resolves, setResolves] = useState<string[]>([]);
  const save = useEvidenceCommand(record.item.id, structureResponseSchema);
  const changes = describe(base, drafts);
  const touched = drafts.filter(
    (x) =>
      !base.some(
        (b) => b.key === x.key && b.start === x.start && b.end === x.end && b.sources.join() === x.sources.join(),
      ),
  );
  const speaking = new Map<string, number>();
  for (const x of drafts) speaking.set(x.speaker ?? "", (speaking.get(x.speaker ?? "") ?? 0) + x.end - x.start);
  for (const x of base) speaking.set(x.speaker ?? "", (speaking.get(x.speaker ?? "") ?? 0) - (x.end - x.start));
  const deltas = [...speaking]
    .filter(([, v]) => Math.abs(v) >= 100)
    .map(
      ([id, v]) =>
        `${record.speakers.find((s) => s.id === id)?.display ?? "Unattributed"} ${v > 0 ? "+" : "−"}${secs(Math.abs(v))}`,
    );
  const com = record.communications.find((c) => c.id === communicationId);
  const others = (com?.copies.list ?? []).filter((c) => !c.current);
  const open = record.problems.filter((p) => p.state === "open" && p.communication === communicationOrdinal);
  return (
    <Dialog
      open
      wide
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Review timing changes"
      onSubmit={() => {
        if (!reason.submit()) return;
        void editing.acquire(communicationId).then(
          (lease) => {
            save.mutate(
              {
                path: `/api/evidence/communications/${communicationId}/structure`,
                body: {
                  lease_id: lease.lease_id,
                  base_revision_id: baseRevision,
                  reason: reason.trimmed,
                  resolves,
                  turns: drafts.map((x) => ({
                    sources: x.sources,
                    ...(x.words ? { words: x.words } : {}),
                    start_ms: x.start,
                    end_ms: x.end,
                    speaker_id: x.speaker,
                  })),
                },
              },
              {
                onSuccess: (out) => {
                  void editing.release();
                  const carried = out.carried.map((c) => `${c.title}: ${CARRIED_STATE[c.state]}`);
                  toast.show(`Timing saved as a new version${carried.length > 0 ? ` · ${carried.join(" · ")}` : ""}`);
                  onSaved();
                },
              },
            );
          },
          () => {
            toast.show("Someone else is editing this transcript. Try again when they finish.");
          },
        );
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          cancel="Back"
          confirm="Save timing"
          pending={save.isPending}
          failed={save.isError}
        />
      }
    >
      <ul className="m-0 list-disc pl-5 text-md">
        {changes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <ul className="m-0 mt-2 list-disc pl-5 text-md text-muted">
        <li>
          {touched.length} turn{touched.length === 1 ? "" : "s"} affected; word timing realigned inside them
        </li>
        <li>No passage checks to rerun</li>
        {deltas.length > 0 ? <li>Speaking time: {deltas.join(" · ")}</li> : null}
        <li>
          Readers and exports are notified through the change feed. Links pinned to a version keep showing that version;
          links to the latest version open the nearest turn of the new timing
        </li>
        <li>
          {others.length === 0
            ? "No other copies get the new timing"
            : `New timing carries over to ${others.map((o) => o.title).join(", ")} where the alignment is confident at both edges`}
        </li>
        <li>Saving creates a new version</li>
      </ul>
      {open.length > 0 ? <OpenReports problems={open} picked={resolves} onPick={setResolves} /> : null}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={save.error} fallback={EVIDENCE_ERROR_FALLBACK} />
    </Dialog>
  );
}
